import type { JSONContent } from '@tiptap/vue-3';
import type { RichTextMediaNodeType } from '~kui/utils/richTextMedia';

import KUICoreInputRichTextEditorRendererEmbed from '~kui/components/KUI/Core/Input/RichTextEditor/Renderer/KUICoreInputRichTextEditorRendererEmbed.vue';
import KUICoreInputRichTextEditorRendererImage from '~kui/components/KUI/Core/Input/RichTextEditor/Renderer/KUICoreInputRichTextEditorRendererImage.vue';
import { isRenderableMediaNode, isRichTextMediaNodeType, toImageOrientation } from '~kui/utils/richTextMedia';
import { toSocialEmbed } from '~kui/utils/socialEmbed';

export const slugifyAnchor = (text: string): string =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-');

/**
 * Returns a unique anchor id for `base`, suffixing `-2`, `-3`… past ids already in
 * `usedIds`, and records the result. Shared so every anchor scheme on a page (body
 * headings, step cards) produces ids under one dedup set.
 */
export const dedupeAnchorId = (base: string, usedIds: Set<string>): string => {
  let id = base;
  for (let suffix = 2; usedIds.has(id); suffix++) {
    id = `${base}-${suffix}`;
  }
  usedIds.add(id);

  return id;
};

const SAFE_LINK_HREF_PATTERN = /^(?:https?:|mailto:|tel:|#|\/)/i;

export const getNodeText = (node: JSONContent): string =>
  node.type === 'text'
    ? (node.text || '')
    : (node.content || []).map(child => getNodeText(child)).join('');

export type HeadingLevel = 2 | 3 | 4 | 5 | 6;

const SOCIAL_EMBED_ROW = 'socialEmbedRow';

const clampHeadingLevel = (heading: JSONContent): HeadingLevel =>
  typeof heading.attrs?.level === 'number' ? (Math.min(Math.max(heading.attrs.level, 2), 6) as HeadingLevel) : 2;

export const extractHeadingAnchors = (doc: JSONContent | null): { id: string; label: string; level: HeadingLevel }[] => {
  const headingNodes = (doc?.content || []).filter(node => node.type === 'heading');
  const usedIds = new Set<string>();

  return headingNodes.map((heading) => {
    const label = getNodeText(heading).trim();
    const level = clampHeadingLevel(heading);
    const base = slugifyAnchor(label);

    if (!base) {
      return { id: '', label, level };
    }

    return { id: dedupeAnchorId(base, usedIds), label, level };
  });
};

const isMediaNode = (node: JSONContent): node is JSONContent & { type: RichTextMediaNodeType } =>
  isRichTextMediaNodeType(node.type);

const isList = (node: JSONContent) => node.type === 'bulletList' || node.type === 'orderedList';

const listItemsOf = (list: JSONContent): JSONContent[] => list.content?.filter(c => c.type === 'listItem') ?? [];

/**
 * Whether the block renderer would draw any media in a doc. It walks the renderer's own shape:
 * media at the root, in a list item's direct children and in a blockquote, never deeper.
 */
export const hasRenderableMedia = (doc: JSONContent): boolean =>
  (doc.content ?? []).some(node => isRenderableMediaNode(node)
    || (node.type === 'blockquote' && (node.content ?? []).some(isRenderableMediaNode))
    || (isList(node) && listItemsOf(node).some(item => (item.content ?? []).some(isRenderableMediaNode))));

const richTextHeadingVariants = cva('pb-2 font-semibold leading-tight', {
  variants: {
    level: {
      2: 'pt-6 text-xl',
      3: 'pt-4 text-lg',
      4: 'pt-4 text-base',
      5: 'pt-4 text-base',
      6: 'pt-4 text-base',
    },
  },
});

export const richTextHeadingClass = (level: HeadingLevel): string =>
  twMerge(richTextHeadingVariants({ level }));

// Back-to-back social posts share one row, so a TikTok and an Instagram post sit side by side.
const groupSocialEmbeds = (nodes: JSONContent[]): JSONContent[] =>
  nodes.reduce<JSONContent[]>((rows, node) => {
    const last = rows.at(-1);
    if (node.type === 'socialEmbed' && last?.type === SOCIAL_EMBED_ROW) {
      last.content?.push(node);
      return rows;
    }
    rows.push(node.type === 'socialEmbed' ? { type: SOCIAL_EMBED_ROW, content: [node] } : node);
    return rows;
  }, []);

export const useRichTextEditorRender = (
  content: ComputedRef<JSONContent | null>,
  options: { anchorHeadings?: boolean; renderBlocks?: boolean; mediaSizes?: string | null } = {},
) => {
  const keepsNestedChild = (node: JSONContent) => node.type === 'paragraph' || (options.renderBlocks === true && isMediaNode(node));

  const rootDocItems = computed(() => {
    if (!content.value?.content) {
      return [];
    }

    // Heading, blockquote and media rendering is opt-in (renderBlocks): stored content across
    // existing consumers can already contain these nodes (the editor's StarterKit input
    // rules create them), and rendering them by default would restyle every consumer.
    const items = content.value.content.filter(c =>
      c.type === 'paragraph' || c.type === 'bulletList' || c.type === 'orderedList'
      || (options.renderBlocks === true && (c.type === 'heading' || c.type === 'blockquote' || isMediaNode(c))),
    );

    return groupSocialEmbeds(items);
  });

  const pipelineFormatting = (text: string, hasSuperscript: boolean = false, hasSubscript: boolean = false, hasStrikethrough: boolean = false) => {
    let base: string | VNode = text;

    if (hasStrikethrough) {
      base = h('s', text);
    }

    if (hasSuperscript) {
      base = h('sup', base);
      return base;
    }

    if (hasSubscript) {
      base = h('sub', base);
      return base;
    }

    return base;
  };

  const renderText = (item: JSONContent) => {
    if (item.type !== 'text') {
      return null;
    }

    const linkMark = item.marks?.find(mark => mark.type === 'link');

    const hasBold = item.marks?.some(mark => mark.type === 'bold');
    const hasItalic = item.marks?.some(mark => mark.type === 'italic');
    const hasStrikethrough = item.marks?.some(mark => mark.type === 'strike');
    const hasUnderline = item.marks?.some(mark => mark.type === 'underline');
    const hasSubscript = item.marks?.some(mark => mark.type === 'subscript');
    const hasSuperscript = item.marks?.some(mark => mark.type === 'superscript');

    const postPipelineFormatted = pipelineFormatting(item.text || '', hasSuperscript, hasSubscript, hasStrikethrough);

    const href = typeof linkMark?.attrs?.href === 'string' && SAFE_LINK_HREF_PATTERN.test(linkMark.attrs.href.trim())
      ? linkMark.attrs.href
      : undefined;

    if (href) {
      return h(
        'a',
        {
          href,
          target: '_blank',
          rel: 'noopener noreferrer',
          class: ['text-foreground underline', hasBold ? 'font-semibold' : '', hasItalic ? 'italic' : ''],
        },
        postPipelineFormatted,
      );
    }

    return h(
      'span',
      {
        class: [hasBold ? 'font-semibold' : '', hasItalic ? 'italic' : '', hasUnderline ? 'underline' : ''],
      },
      postPipelineFormatted,
    );
  };

  const renderParagraphLine = (paragraph: JSONContent) => {
    const texts = paragraph.content?.filter(c => c.type === 'text') || [];
    if (texts.length === 0) {
      return h('br');
    }

    return h(
      'p',
      { class: 'py-2 leading-snug' },
      texts?.map(text => (!!text.marks || !!text.attrs ? renderText(text) : text.text)),
    );
  };

  const positiveNumberOrNull = (value?: number | null) => (typeof value === 'number' && value > 0 ? value : null);

  const renderImage = (image: JSONContent) => {
    const attrs = image.attrs;
    if (!attrs || !isRenderableMediaNode(image) || typeof attrs.src !== 'string') {
      return null;
    }

    return h('div', { class: 'my-6 flex justify-center' }, h(KUICoreInputRichTextEditorRendererImage, {
      src: attrs.src.trim(),
      alt: typeof attrs.alt === 'string' ? attrs.alt : '',
      caption: typeof attrs.caption === 'string' ? attrs.caption : '',
      orientation: toImageOrientation(attrs.orientation),
      width: positiveNumberOrNull(attrs.width),
      height: positiveNumberOrNull(attrs.height),
      sizes: options.mediaSizes ?? null,
    }));
  };

  const renderSocialEmbedRow = (row: JSONContent) => {
    const embeds = (row.content || [])
      .filter(isRenderableMediaNode)
      .map(node => toSocialEmbed({ provider: node.attrs?.provider, id: node.attrs?.id }))
      .filter(embed => embed !== null);

    if (embeds.length === 0) {
      return null;
    }

    return h(
      'div',
      { 'class': 'my-6 flex flex-wrap items-start gap-4', 'data-testid': 'rte-renderer-embed-row' },
      embeds.map(embed => h(KUICoreInputRichTextEditorRendererEmbed, { embed })),
    );
  };

  const renderMediaNode = (node: JSONContent) =>
    node.type === 'image' ? renderImage(node) : renderSocialEmbedRow(node);

  const renderListItem = (listItem: JSONContent) => {
    const children = groupSocialEmbeds(listItem.content?.filter(keepsNestedChild) || []);

    return h(
      'li',
      { class: 'ml-5' },
      children.map((child) => {
        if (child.type !== 'paragraph') {
          return renderMediaNode(child);
        }
        const texts = child.content?.filter(c => c.type === 'text') || [];
        if (texts.length === 0) {
          return h('br');
        }
        return texts.map(text => (!!text.marks || !!text.attrs ? renderText(text) : text.text));
      }),
    );
  };

  const renderBulletList = (bulletList: JSONContent) => {
    const listItems = listItemsOf(bulletList);

    return h(
      'ul',
      { class: 'list-disc py-2' },
      listItems.map(item => renderListItem(item)),
    );
  };

  const renderOrderedList = (orderedList: JSONContent) => {
    const listItems = listItemsOf(orderedList);

    return h(
      'ol',
      { class: 'list-decimal py-2' },
      listItems.map(item => renderListItem(item)),
    );
  };

  // Computed once per doc so duplicate headings get deterministically deduped unique ids,
  // keyed by node identity so each renderHeading call is a single Map lookup rather than a rescan.
  const headingAnchors = computed(() => {
    const headingNodes = (content.value?.content || []).filter(node => node.type === 'heading');
    const anchors = extractHeadingAnchors(content.value);

    return new Map(headingNodes.map((node, index) => [node, anchors[index]]));
  });

  const renderHeading = (heading: JSONContent) => {
    const anchor = headingAnchors.value.get(heading);
    const level = anchor?.level ?? clampHeadingLevel(heading);
    const texts = heading.content?.filter(c => c.type === 'text') || [];

    return h(`h${level}`, {
      // Never emit id="" — an empty anchor id (symbol/whitespace-only heading text) is invalid HTML.
      id: options.anchorHeadings && anchor?.id ? anchor.id : undefined,
      class: richTextHeadingClass(level),
    }, texts.map(text => (!!text.marks || !!text.attrs ? renderText(text) : text.text)));
  };

  const renderBlockquote = (blockquote: JSONContent) => {
    const children = groupSocialEmbeds(blockquote.content?.filter(keepsNestedChild) || []);

    return h(
      'blockquote',
      { class: 'my-2 border-l-4 border-secondary pl-6 font-semibold' },
      children.map(child => (child.type === 'paragraph' ? renderParagraphLine(child) : renderMediaNode(child))),
    );
  };

  const renderNode = (node: JSONContent) => {
    if (node.type === 'bulletList') {
      return renderBulletList(node);
    }
    if (node.type === 'orderedList') {
      return renderOrderedList(node);
    }
    if (node.type === 'paragraph') {
      return renderParagraphLine(node);
    }
    if (node.type === 'heading') {
      return renderHeading(node);
    }
    if (node.type === 'blockquote') {
      return renderBlockquote(node);
    }
    if (node.type === 'image') {
      return renderImage(node);
    }
    if (node.type === SOCIAL_EMBED_ROW) {
      return renderSocialEmbedRow(node);
    }
    return null;
  };

  return {
    rootDocItems,
    renderParagraphLine,
    renderText,
    renderBulletList,
    renderOrderedList,
    renderListItem,
    renderHeading,
    renderBlockquote,
    renderNode,
  };
};
